import {isFeatureEnabled} from '@github-ui/feature-flags'
import {getAutomatedReviewer} from '@github-ui/hovercards/automated-reviewers'
import type {
  GetHovercardAttributesForTypeFn,
  HovercardAttributesResult,
} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {Label, Link} from '@primer/react'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {isCopilotReviewer} from '../../overview/reviewers/copilot-reviewer'
import styles from './TimelineAuthor.module.css'

type Props = {
  login?: string | null
  avatarUrl?: string | null
  slug?: string | null
}

export function TimelineAuthor({login, avatarUrl, slug}: Props) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()

  if (!login) return null
  const isAI = isCopilotReviewer({displayLogin: login, slug: slug ?? ''})
  const automatedReviewer = isFeatureEnabled('automated_review_comment_reviewer_identity')
    ? getAutomatedReviewer(slug ?? undefined)
    : undefined
  const profileUrl = slug ? `/apps/${slug}` : `/${isAI ? 'apps/' : ''}${login}`
  const hovercardAttributes = getAuthorHovercardAttributes({
    getHovercardAttributesForType,
    isAI,
    automatedReviewerSlug: isFeatureEnabled('automated_review_comment_reviewer_hovercard')
      ? automatedReviewer?.slug
      : undefined,
    login,
    slug,
  })

  return (
    // margin right here to make sure timeline text lines up correctly when no author
    <span className="mr-1">
      <Link
        href={profileUrl}
        {...hovercardAttributes}
        aria-label={`${login}'s profile`}
        className={`${styles.author} fgColor-default`}
      >
        {avatarUrl && <GitHubAvatar className={styles.avatar} src={avatarUrl} size={16} />}
        <strong className={`fgColor-default${avatarUrl ? ' ml-1' : ''}`}>{login}</strong>
      </Link>
      {isAI && (
        <Label variant="secondary" className="ml-1">
          AI
        </Label>
      )}
    </span>
  )
}

// Precedence: Copilot > known automated reviewer > unrecognized app (no hovercard) > regular user.
function getAuthorHovercardAttributes({
  getHovercardAttributesForType,
  isAI,
  automatedReviewerSlug,
  login,
  slug,
}: {
  getHovercardAttributesForType: GetHovercardAttributesForTypeFn
  isAI: boolean
  automatedReviewerSlug: string | undefined
  login: string
  slug?: string | null
}): HovercardAttributesResult {
  if (isAI) return getHovercardAttributesForType('copilot', {botSlug: slug || login})
  if (automatedReviewerSlug)
    return getHovercardAttributesForType('automated_reviewer', {botSlug: automatedReviewerSlug})
  if (slug) return {}
  return getHovercardAttributesForType('user', {login})
}
